import type { RenderSegment } from '../components/ProjectSegment/ProjectSegment.types'

export interface NewTaskTargetInput {
  /** Die Segmente, wie die Liste sie zeigt — der Index geht an `startCreating`. */
  segments: RenderSegment[]
  /** Die markierte Zeile, falls eine: eine Aufgabe oder ein Abschnitts-Kopf. */
  focused: { type: 'task' | 'section'; id: string } | null
  /** Die Liste zeigt nur einen Abschnitt (Listen-Modus mit gewähltem Abschnitt). */
  singleSection: boolean
}

export interface NewTaskTarget {
  /** `'above'`: ohne Abschnitt über dem ersten Abschnitt — die Gruppe ohne Abschnitt gibt es noch nicht. */
  position: 'top' | 'bottom' | 'above'
  segmentIndex: number
  /** Ein eingeklappter Ziel-Abschnitt muss erst auf — sonst landete die Aufgabe unsichtbar. */
  expandSectionId: string | null
}

/*
  Ein leeres Segment hat nur den unteren Ersteller (wie die Taste `A`). Gemessen an ALLEN Aufgaben: Ein
  eingeklappter Abschnitt zeigt keine, klappt aber erst auf und hat dann welche.
*/
function topOf(segments: RenderSegment[], segmentIndex: number): NewTaskTarget {
  const segment = segments[segmentIndex]!
  return {
    position: (segment.allTasks ?? segment.tasks).length > 0 ? 'top' : 'bottom',
    segmentIndex,
    expandSectionId: segment.collapsed && segment.section ? segment.section.id : null,
  }
}

/**
 * Wohin „Neue Aufgabe" im Projekt anlegt — der Knopf der **Listen-Werkzeug-Pille**, ⌘N und das Menü (#272):
 * immer **oben**. Markierte Aufgabe oder markierter Kopf → oben in ihrem Abschnitt; sonst ganz oben ohne
 * Abschnitt. Zeigt die Liste nur einen Abschnitt, oben in ihm — darüber läge die Aufgabe außerhalb der Ansicht.
 *
 * @example
 * const { position, segmentIndex, expandSectionId } = resolveNewTaskTarget({ segments, focused, singleSection })
 * startCreating(position, segmentIndex)
 */
export function resolveNewTaskTarget({ segments, focused, singleSection }: NewTaskTargetInput): NewTaskTarget {
  if (focused) {
    const own = segments.findIndex(segment =>
      focused.type === 'section' ? segment.section?.id === focused.id : segment.tasks.some(task => task.id === focused.id))
    if (own !== -1) return topOf(segments, own)
  }
  if (singleSection || !segments[0]?.section) return topOf(segments, 0)
  return { position: 'above', segmentIndex: 0, expandSectionId: null }
}
